import { LayerViewModel } from "@omnia/shared/layers/layerViewModel"
import { getApplicationAnalysis } from "../../../analysis/analysis"
import { FieldViewModel, IDatalayerViewerFactory, LayerTypeId } from "../../../omnia"
import { INtrApp, INutrientAppLayer, INutrientAppZone, INutritionPlan, INutritionPlanProductApp, NutrientType } from "../../../types"
import { getNutrientKeys } from "../../../utils"
import { INutritionMapViewContext } from "../types"
import { NutritionMapView } from "./view"
import { sortDates } from "@omnia/shared/utils/utils"
import { DatalayerZone } from "@omnia/shared/layers/datalayerZone"
import { MapGeometryType } from "@omnia/shared/generated/enums"
import { NutrientAppLayerViewerFactory } from "@omnia/shared/nutrition/map/nutrientAppLayerViewer"
import { NutrientAppZone } from "@omnia/shared/nutrition/map/nutrientAppZone"

export class PlanMapView extends NutritionMapView {

    static create(ctxt: INutritionMapViewContext, plan: INutritionPlan, app: INutritionPlanProductApp) {

        let key = `app:${app.NutritionPlanProductAppId}`;
        let stockName = app.Manure ? app.Manure.Name : app.Fertiliser.Name;
        let appDate = app.ApplicationDate.toLocaleDateString();
        let name = `${plan.Name} - ${stockName} - ${appDate}`;

        let analysis = getApplicationAnalysis(app);

        let views = getNutrientKeys().map(n => {

            if (analysis && analysis[n]) {
                let nutrientType = NutrientType[n];
                return new PlanMapView(name, key, ctxt, plan, app, nutrientType);
            }

        }).filter(v => !!v);

        return views;

    }

    private _showNutrientRates: boolean;

    constructor(name: string, key: string, private ctxt: INutritionMapViewContext, private plan: INutritionPlan, private app: INutritionPlanProductApp, nutrientType: NutrientType) {

        super(name, key, nutrientType);

        const layerTypeId = LayerTypeId.NutrientApplications;

        this._layerType = ctxt.nutritionSvc.getDataLayerType(layerTypeId);

        let layerViewer = NutrientAppLayerViewerFactory.create(nutrientType, false);

        this._layerViewer = layerViewer;

        this.setupColors(layerViewer.colors);

    }

    public getPlanId() {
        return this.app.NutritionPlanId
    }

    public getApplicationId(): number {
        return this.app.NutritionPlanProductAppId;
    }

    get showNutrientRates(): boolean {
        return this._showNutrientRates;
    }

    set showNutrientRates(value: boolean) {
        this._showNutrientRates = value;
        let viewer = NutrientAppLayerViewerFactory.create(this._nutrient, false, value);
        this.resetViewer(viewer);
        this.refreshViewers();
    }

    protected resetViewer(viewer: IDatalayerViewerFactory) {

        this._layerViewer = viewer;

        this.getLayers().forEach(l => {
            l.setViewer(viewer);
        });

    }

    protected createLayers() {

        let app = this.app;

        let ctxt = this.ctxt.datalayerContext;

        let nutrientKey = NutrientType[this._nutrient];

        let cropping = this.ctxt.fieldSvc.cropping;

        cropping = cropping.filter(c => c.hasBoundary());

        return this.plan.Cropping
            .map(pc => cropping.find(c => c.croppingId == pc.CroppingId))
            .filter(c => !!c)
            .sort((a, b) => sortDates(a.startDate, b.startDate))
            .map(c => {

                let zones = this.ctxt.planReader.getApplications(c.croppingId, app.NutritionPlanId, app.NutritionPlanProductAppId);

                if (zones) {

                    let datalayer = this.createVirtualLayer(c, zones, app, nutrientKey);

                    let vm = new LayerViewModel(ctxt, this.ctxt.map, datalayer, c.fieldVm);

                    vm.setViewer(this._layerViewer);

                    return vm;

                }

            })
            .filter(i => !!i);

    }

    protected createVirtualLayer(cropping: FieldViewModel, appZones: INtrApp[], app: INutritionPlanProductApp, nutrientKey: string) {

        let zones: INutrientAppZone[] = appZones.map(appZone => {

            const name = appZone.Zone ? appZone.ZoneNumber.toString() : cropping.fieldName;
            const geom = appZone.Geometry || cropping.cropping.Boundary;
            const area = appZone.Geometry ? appZone.Area : (cropping.area * 10000)
            const productRate = appZone.ProductRate;
            const nutrientRate = appZone[nutrientKey];

            const rounding = (this._nutrientKey == "Lime" ? 2 : 1);

            return {

                DataLayerZoneId: appZone.NtrAppId,
                Number: appZone.ZoneNumber,
                Geometry: geom,
                Area: area,
                Value: productRate,
                Name: name,
                Data: appZone,

                ProductRate: productRate,
                NutrientRate: nutrientRate,
                ProductAmount: Math.round((appZone.ProductRate * appZone.Area / 10000) * rounding) / rounding,

            }

        });

        let layer: INutrientAppLayer = {

            DataLayerId: 0,
            GeometryType: MapGeometryType.Polygon,
            TypeId: this._layerType.TypeId,
            Type: this._layerType,
            FieldId: cropping.fieldId,
            CroppingId: cropping.croppingId,
            Zones: zones,

            Application: {
                PlanName: this.plan.Name,
                TargetNutrient: this._nutrient,
                ProductName: null,
                Units: null,
            },

        }

        return layer;

    }

    protected applyZoneOverride(zone: DatalayerZone, ovr: any, refresh: boolean) {

        if (zone instanceof NutrientAppZone) {
            if (ovr) {

                let productRate = ovr.productRate;
                let nutrientRate = ovr.nutrientRates[this._nutrientKey];

                zone.setValues(productRate, nutrientRate);

            }
            else {

                zone.resetToLayerValues();

            }

            if (refresh) {
                zone.refresh();
                this.ctxt.map.refreshSelectInteraction();
            }
        }

    }

}
